import { API_BASE_URL } from './config';
import type {
  BillingPlan,
  BillingOverview,
  TransactionsPage,
  TransactionHistoryPage,
  UsageDetailResponse,
} from './types/billing';

export type {
  BillingPlan,
  BatchSummary,
  BillingOverview,
  CreditTransaction,
  TransactionsPage,
  ConversationUsageSummary,
  ProcessRunUsageSummary,
  MessageRequestDetail,
  TransactionHistoryItem,
  TransactionHistoryPage,
  UsageDetailResponse,
} from './types/billing';

function authHeaders(token: string) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * Create a Stripe Checkout session for a subscription plan.
 */
export async function billingCheckout(
  token: string,
  planTierId: string,
  period: 'MONTHLY' | 'YEARLY' = 'MONTHLY'
): Promise<{ checkoutUrl: string }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/billing/checkout`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ planTierId, period }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Checkout failed');
  return data;
}

/**
 * Create a Stripe Checkout session for purchasing on-demand credits.
 */
export async function billingPurchaseCredits(
  token: string,
  quantity: number
): Promise<{ checkoutUrl: string }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/billing/credits/purchase`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ quantity }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Purchase failed');
  return data;
}

/**
 * Get Stripe Customer Portal URL for self-service billing management.
 */
export async function billingGetPortal(token: string): Promise<{ portalUrl: string }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/billing/portal`, {
    headers: authHeaders(token),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Portal access failed');
  return data;
}

/**
 * Confirm a Stripe Checkout session (fallback for webhook delays).
 */
export async function billingConfirm(
  token: string,
  sessionId: string
): Promise<{ billingStatus: string }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/billing/confirm`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ sessionId }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Confirm failed');
  return data;
}

/**
 * Get current credit balance and billing status.
 */
export async function billingGetBalance(
  token: string
): Promise<{ credits: number; billingStatus: string }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/billing/balance`, {
    headers: authHeaders(token),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Balance fetch failed');
  return data;
}

/**
 * Get paginated transaction history.
 */
export async function billingGetTransactions(
  token: string,
  page = 0,
  size = 20
): Promise<TransactionsPage> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/billing/transactions?page=${page}&size=${size}`,
    { headers: authHeaders(token) }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Transactions fetch failed');
  return data;
}

/**
 * Get available subscription plans from the backend (no Stripe IDs exposed).
 */
export async function billingGetPlans(token: string): Promise<BillingPlan[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/billing/plans`, {
    headers: authHeaders(token),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Plans fetch failed');
  return data;
}

/**
 * Activate the free tier for a personal org (one-time, no card required).
 */
export async function billingActivateFreeTier(
  token: string
): Promise<{ billingStatus: string; planType: string }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/billing/activate-free-tier`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Failed to activate free tier');
  return data;
}

/**
 * Get comprehensive billing overview for the settings page.
 */
export async function billingGetOverview(token: string): Promise<BillingOverview> {
  const response = await fetch(`${API_BASE_URL}/api/v1/billing/overview`, {
    headers: authHeaders(token),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Overview fetch failed');
  return data;
}

/**
 * Get grouped transaction history (conversations + standalone transactions).
 */
export async function billingGetUsage(
  token: string,
  page = 0,
  size = 20
): Promise<TransactionHistoryPage> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/billing/usage?page=${page}&size=${size}`,
    { headers: authHeaders(token) }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Usage fetch failed');
  return data;
}

/**
 * Get detailed usage breakdown for a specific conversation.
 */
export async function billingGetConversationUsage(
  token: string,
  conversationId: string
): Promise<UsageDetailResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/billing/usage/${encodeURIComponent(conversationId)}`,
    { headers: authHeaders(token) }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Conversation usage fetch failed');
  return data;
}
